import { useState } from 'react'
import { useStore } from '../../store'
import type { ServiceStatus } from '../../store'
import { Toggle } from '../ui'

/**
 * Labels and downCopy transcribed verbatim from
 * docs/design-handoff/data/settings-model.json's `services` array.
 */
const SERVICES: Array<{ key: keyof ServiceStatus; label: string; downCopy: string }> = [
  {
    key: 'relay',
    label: 'Chat relay',
    downCopy: 'Chat cannot send. Live chat threads are frozen until the connection is back.',
  },
  {
    key: 'halo',
    label: 'Halo',
    downCopy: 'Halo is unreachable. Notes and close notes are held and sync when it returns.',
  },
  {
    key: 'sms',
    label: 'SMS adapter',
    downCopy: 'SMS is offline. Texts are not going out or coming in.',
  },
  {
    key: 'voice',
    label: 'Voice adapter',
    downCopy: 'Voice events are not arriving. Calls will not appear in the rail.',
  },
]

/**
 * System status chip + its 300px panel — docs/design-handoff/tossker-redesign/
 * README.md §1 "Header": pill, 1px `--border-subtle`, a `--status-success`
 * dot, "All systems up". The per-service toggle here is a prototype
 * stand-in: it only calls `setSvc` to flip the simulated state in the
 * store. A real outage is reported by health polling (Task 23), which
 * pushes the same `setSvc` shape through the SSE `service` event — this
 * control never calls an operation itself, so Task 23 can wire in behind it
 * without this component changing.
 */
export function StatusChip() {
  const svc = useStore((s) => s.svc)
  const setSvc = useStore((s) => s.setSvc)
  const streamStatus = useStore((s) => s.streamStatus)
  const [open, setOpen] = useState(false)

  const downCount = SERVICES.filter((s) => !svc[s.key]).length
  // A dropped live feed is its own kind of down, and the one the reader most
  // needs to know about: every other service can be healthy while this pane
  // silently stops updating. It is surfaced in the chip itself rather than
  // only in the panel, because nobody opens the panel to check whether the
  // thing that looks fine is fine.
  const feedDown = streamStatus === 'reconnecting'
  const allUp = downCount === 0 && !feedDown
  const statusLabel = allUp
    ? 'All systems up'
    : feedDown && downCount === 0
      ? 'Live feed reconnecting'
      : `${downCount} down`

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        // The WORDS go on a phone, not the chip: "All systems up" is three
        // words competing with the brand for a 390pt row, and the dot already
        // carries the state (green up, red pulsing down). `aria-label` keeps
        // it a labelled control there — `hidden` removes the text from the
        // accessibility tree too, so without this it would be a bare button.
        aria-label={statusLabel}
        className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[20px] border border-subtle px-2 py-1 text-[13px] font-medium md:px-2.5"
      >
        <span
          aria-hidden="true"
          className={['h-[7px] w-[7px] shrink-0 rounded-full', allUp ? 'bg-ok' : 'bg-bad animate-cc-pulse'].join(' ')}
        />
        <span className={['hidden whitespace-nowrap md:inline', allUp ? 'text-ok' : 'text-bad'].join(' ')}>{statusLabel}</span>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-[300px] rounded-[10px] border border-subtle bg-elevated p-2 shadow-menu"
        >
          {feedDown ? (
            <div className="flex flex-col gap-1 border-b border-subtle px-1 py-2">
              <span className="text-[13px] text-hi">Live feed</span>
              <p className="text-[11px] text-lo">
                Reconnecting. This pane is not updating; it will catch up automatically.
              </p>
            </div>
          ) : null}
          {SERVICES.map((s) => {
            const up = svc[s.key]
            return (
              <div key={s.key} className="flex flex-col gap-1 border-b border-subtle px-1 py-2 last:border-b-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] text-hi">{s.label}</span>
                  <Toggle checked={up} onChange={(checked) => setSvc({ [s.key]: checked })} aria-label={`Toggle ${s.label}`} />
                </div>
                {!up ? <p className="text-[11px] text-lo">{s.downCopy}</p> : null}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
